/**
 * Navigation-chart data assembly shared by the phone client and the headless tests: tile world origins, the world-space
 * node list, the gateway list and the stitched connection graph, all derived from a map definition plus its tiles' companion
 * JSON. No DOM or Node APIs (runs in both).
 */
import type { MapDefinition } from './types.js';
import { getSourceGlbTileGridOffset, getTileVerticalOffset } from './tiledGlb.js';
import { aggregateTileConnections, type AggregatedConnection, type TileJson, type TileJsonNode } from './tileJson.js';

export type TileOrigin = { x: number; z: number; y: number };

export type NavNodeEntry = { node: TileJsonNode; tileId: string; chunkRegion: string; biome: string; tempC: number; tileKind?: string };

export type NavGatewayData = { key: string; side: string; x: number; y: number; z: number; radius: number; nodeKeys: string[] };

/** Key a tile's companion JSON is stored under: the GLB file's base name. */
export const tileKeyForGlb = (glbPath: string): string => glbPath.replace(/\.glb$/i, '').split('/').pop() ?? glbPath;

/**
 * Nav-chart identity of a tile slot. A GLB file used by one slot keeps its file-stem key (saved reveal sets stay valid); a library
 * tile shared by several slots is keyed `<stem>@<tileX>_<tileY>_<tileZ>` so each slot keeps its own nodes and origin.
 */
export const tileKeyForSlot = (td: { sourceGlbFile: string; tileX: number; tileY?: number; tileZ: number }, all: ReadonlyArray<{ sourceGlbFile: string }>): string => {
  const stem = tileKeyForGlb(td.sourceGlbFile);
  return all.filter((t) => t.sourceGlbFile === td.sourceGlbFile).length > 1 ? `${stem}@${td.tileX}_${td.tileY ?? 0}_${td.tileZ}` : stem;
};

/**
 * World (map-space) origin of every tile's centre, keyed by tile key. Tile JSON node positions are centre-relative; the
 * GLB geometry is edge-relative, so the centre is the tile edge plus half a tile. `y` is the tile layer's vertical offset.
 */
export const computeTileOrigins = (mapDef: MapDefinition): Map<string, TileOrigin> => {
  const out = new Map<string, TileOrigin>();
  const sourceTiles = mapDef.sourceGlbTiles ?? [];
  const tileSize = mapDef.sourceGlbTileSizeMeters ?? { X: 0, Y: 0, Z: 0 };
  const gridOriginOffset = getSourceGlbTileGridOffset(sourceTiles, tileSize, mapDef.sourceGlbGridBounds);
  for (const td of sourceTiles) {
    const edgeX = td.tileX * tileSize.X - gridOriginOffset.x;
    const edgeZ = td.tileZ * tileSize.Z - gridOriginOffset.z;
    const cx = td.sourceGlbCenter?.x ?? mapDef.sourceGlbCenter?.x ?? 0;
    const cz = td.sourceGlbCenter?.z ?? mapDef.sourceGlbCenter?.z ?? 0;
    out.set(tileKeyForSlot(td, sourceTiles), {
      x: edgeX + tileSize.X / 2 - cx,
      z: edgeZ + tileSize.Z / 2 - cz,
      y: getTileVerticalOffset(td.tileY ?? 0, tileSize)
    });
  }
  return out;
};

/** Nodes (shifted into map space + layered Y), open gateways and stitched connections for the tiles that are loaded. */
export const buildNavData = (
  loaded: Map<string, TileJson>,
  origins: Map<string, { x: number; z: number; y?: number }>
): { nodeEntries: NavNodeEntry[]; connections: AggregatedConnection[]; gateways: NavGatewayData[] } => {
  const tileOffsets = new Map<string, { x: number; z: number; y?: number }>([...origins.entries()].filter(([tileId]) => loaded.has(tileId)));
  const connections = aggregateTileConnections(loaded, tileOffsets);
  const nodeEntries: NavNodeEntry[] = [];
  const gateways: NavGatewayData[] = [];
  for (const [tileId, tj] of loaded) {
    const { x: ox, z: oz, y: oy = 0 } = tileOffsets.get(tileId) ?? { x: 0, z: 0 };
    const { chunk_region: chunkRegion, biome, temp_c: tempC } = tj.metadata;
    for (const node of tj.nodes) {
      if (node.kind === 'node') {
        nodeEntries.push({ node: { ...node, x: node.x + ox, y: node.y + oy, z: node.z + oz }, tileId, chunkRegion, biome, tempC, tileKind: tj.generation?.kind });
      } else if (node.open) {
        const nodeKeys: string[] = [];
        for (const conn of tj.connections) {
          const other = conn.from === node.id ? conn.to : conn.to === node.id ? conn.from : null;
          if (other) nodeKeys.push(`${tileId}:${other}`);
        }
        gateways.push({ key: `${tileId}:${node.id}`, side: node.side ?? '', x: node.x + ox, y: node.y + oy, z: node.z + oz, radius: node.radius_xz, nodeKeys });
      }
    }
  }
  return { nodeEntries, connections, gateways };
};
